import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { useController, useFormContext, useWatch } from 'react-hook-form';

import type { Partner, WelcomeBonusCampaign, WelcomeBonusScope } from '../../../../@types/api';
import { QUERY_KEYS, WELCOME_BONUS_SCOPE_OPTIONS } from '../../../../constants';
import { WELCOME_BONUS_SCOPE } from '../../../../constants/enums';
import { USER_ROLES } from '../../../../constants/keycloak';
import { useMarketplace } from '../../../../hooks/useMarketplace';
import type { WelcomeBonusCreateFormData, WelcomeBonusEditFormData } from '../../../../schema';
import { formatSnakeCaseString } from '../../../../utils';
import PartnerSelect from '../../../molecules/PartnerSelect';
import RetryAlert from '../../../molecules/RetryAlert';
import { WelcomeBonusReviewField, WelcomeBonusReviewGrid } from './WelcomeBonusFormPrimitives';

const ACTIVE_AFFILIATE_QUERY_PARAMS = {
  filters: {
    isActive: true,
  },
  roleTypes: [USER_ROLES.AFFILIATE],
} as const;

const isAffiliateAvailable = (affiliate: Partner, selectedAffiliateIds: string[]) =>
  affiliate.isActive || selectedAffiliateIds.includes(affiliate.userId);

export const getWelcomeBonusScopeOptions = (hasAffiliateSystem: boolean) =>
  WELCOME_BONUS_SCOPE_OPTIONS.map((option) => ({
    ...option,
    disabled: option.id === WELCOME_BONUS_SCOPE.SPECIAL && !hasAffiliateSystem,
  }));

export const WelcomeBonusAudienceCreateFields = () => {
  const { hasAffiliateSystem } = useMarketplace();
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<WelcomeBonusCreateFormData>();
  const {
    field: { ref, onChange, value: welcomeBonusScope, ...scopeField },
  } = useController({ control, name: 'welcomeBonusScope' });
  const scopeOptions = getWelcomeBonusScopeOptions(hasAffiliateSystem);

  const handleScopeChange = (event: SelectChangeEvent<WelcomeBonusScope>) => {
    const nextScope = event.target.value as WelcomeBonusScope;
    onChange(nextScope);

    if (nextScope !== WELCOME_BONUS_SCOPE.GLOBAL) return;

    // Global campaigns never retain stale affiliate targeting after a Scope change.
    setValue('affiliateIds', [], { shouldDirty: true, shouldValidate: true });
  };

  return (
    <>
      <FormControl fullWidth required error={!!errors.welcomeBonusScope}>
        <InputLabel id='select-scope-label'>Scope</InputLabel>
        <Select
          {...scopeField}
          labelId='select-scope-label'
          id='select-scope'
          label='Scope'
          value={welcomeBonusScope ?? ''}
          onChange={handleScopeChange}
          inputRef={ref}
        >
          {scopeOptions.map((option) => (
            <MenuItem key={option.id} value={option.id} disabled={option.disabled}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
        <FormHelperText>{errors.welcomeBonusScope?.message}</FormHelperText>
      </FormControl>
      {welcomeBonusScope === WELCOME_BONUS_SCOPE.SPECIAL && (
        <PartnerSelect<WelcomeBonusCreateFormData>
          control={control}
          name='affiliateIds'
          label='Affiliates'
          queryKey={[QUERY_KEYS.partners, 'affiliateIds']}
          queryParams={ACTIVE_AFFILIATE_QUERY_PARAMS}
          required
          error={errors.affiliateIds}
        />
      )}
    </>
  );
};

export type AssignedAffiliateOptionsState = {
  options: Partner[];
  isFetching: boolean;
  isError: boolean;
  retry: () => void;
};

type WelcomeBonusAudienceEditFieldsProps = {
  campaign: WelcomeBonusCampaign;
  assignedAffiliates: AssignedAffiliateOptionsState;
};

export const WelcomeBonusAudienceEditFields = ({
  campaign,
  assignedAffiliates,
}: WelcomeBonusAudienceEditFieldsProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<WelcomeBonusEditFormData>();
  const affiliateIds = useWatch({ control, name: 'affiliateIds' });
  const isSpecial = campaign.welcomeBonusScope === WELCOME_BONUS_SCOPE.SPECIAL;
  const scopeLabel = campaign.welcomeBonusScope && formatSnakeCaseString(campaign.welcomeBonusScope);

  return (
    <>
      <WelcomeBonusReviewGrid>
        <WelcomeBonusReviewField label='Scope' value={scopeLabel} />
      </WelcomeBonusReviewGrid>
      {isSpecial && (
        <>
          <PartnerSelect<WelcomeBonusEditFormData>
            control={control}
            name='affiliateIds'
            label='Affiliates'
            queryKey={[QUERY_KEYS.partners, 'affiliateIds', affiliateIds]}
            queryParams={ACTIVE_AFFILIATE_QUERY_PARAMS}
            initialOptions={assignedAffiliates.options}
            isOptionAvailable={isAffiliateAvailable}
            disabled={assignedAffiliates.isFetching || assignedAffiliates.isError}
            required
            error={errors.affiliateIds}
          />
          {assignedAffiliates.isError && (
            <RetryAlert onRetry={assignedAffiliates.retry}>Unable to load assigned Affiliates.</RetryAlert>
          )}
        </>
      )}
    </>
  );
};
